import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Chip, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

/**
 * This month's care-management codes earned from logged program minutes and RPM reading days.
 * "Add" creates a pending billing record for a biller to confirm.
 */
function BillingSuggestions({ apiBase, patientId, billingRecords, onAdded }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await fetch(`${apiBase}/patients/${patientId}/billing-suggestions`);
      setData(res.ok ? await res.json() : null);
    } catch {
      setData(null);
    }
  }, [apiBase, patientId]);

  // Reload when billing records change (e.g. after adding one)
  useEffect(() => { load(); }, [load, billingRecords]);

  const add = async (item) => {
    setAdding(item.code);
    try {
      const res = await fetch(`${apiBase}/billing-records`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: patientId,
          service_date: new Date().toISOString().slice(0, 10),
          service_code: item.code,
          description: item.units > 1 ? `${item.description} (x${item.units})` : item.description,
          diagnosis_codes: "",
          provider: "Care Coordinator",
          amount: 0,
          status: "pending",
          notes: `Suggested from ${data.month} program minutes`,
        }),
      });
      if (res.ok && onAdded) onAdded();
    } finally {
      setAdding(null);
    }
  };

  if (!data) return null;
  const programs = Object.entries(data.minutes || {});
  const hasActivity = programs.length > 0 || data.rpm_days > 0;

  return (
    <Box sx={{ mb: 2, p: 1.25, border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
      <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
        <AutoAwesomeIcon sx={{ fontSize: 16, color: "primary.main" }} />
        <Typography variant="caption" fontWeight={700}>This month's billable time ({data.month})</Typography>
      </Box>
      {!hasActivity ? (
        <Typography variant="caption" color="text.secondary">
          No program minutes logged this month. Minutes entered in the encounter drawer appear here when the call is completed.
        </Typography>
      ) : (
        <>
          <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
            {programs.map(([program, minutes]) => (
              <Chip key={program} size="small" variant="outlined" label={`${program} ${minutes} min`} />
            ))}
            {data.rpm_days > 0 && <Chip size="small" variant="outlined" label={`RPM ${data.rpm_days} reading days`} />}
          </Box>
          {data.earned.map((item) => (
            <Box key={item.code} display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ py: 0.25 }}>
              <Typography variant="caption">
                <b>{item.code}</b>{item.units > 1 ? ` x${item.units}` : ""} · {item.description}
              </Typography>
              {item.billed ? (
                <Chip size="small" color="success" label="Billed" />
              ) : (
                <Button size="small" disabled={adding === item.code} onClick={() => add(item)}>Add</Button>
              )}
            </Box>
          ))}
          {data.next.map((n) => (
            <Typography key={`${n.program}-${n.code}`} variant="caption" display="block" color="text.secondary">
              {n.minutes_to_go != null
                ? `${n.minutes_to_go} more ${n.program} min unlocks ${n.code}`
                : `${n.days_to_go} more reading days unlock ${n.code}`}
            </Typography>
          ))}
        </>
      )}
    </Box>
  );
}

BillingSuggestions.propTypes = {
  apiBase: PropTypes.string.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  billingRecords: PropTypes.array,
  onAdded: PropTypes.func,
};

export default BillingSuggestions;
